/**
 * Knoppen en velden in de vormtaal van de webshop en Mijn omgeving. Gedeeld met
 * bouwstenen in ui/ (zoals SlotPicker) die ook in het beheer terugkomen.
 * Het beheer heeft een eigen, compactere set in admin/ui/styles.ts.
 *
 * Eén bron voor elke knop op de publieke site (docs/VORMGEVING.md §5):
 * - `btnPrimary` / `btnSecondary`: compact, voor lades, formulieren en Mijn omgeving.
 * - `btnPrimaryLg` / `btnSecondaryLg`: de hoofdactie van een pagina of sectie (52px hoog).
 * - `btnOnClay`: de hoofdactie op een klei-vlak.
 * - `btnPrice`: een koopactie met het bedrag erin ("Protocol in de mand · € 189"). Links de actie,
 *   rechts het bedrag. Overal hetzelfde: kaart, lade, koopbalk, afrekenen.
 * Indrukken (active) schaalt kort naar 98%: voelbaar, ook op de telefoon zonder hover.
 */

const press = 'transition-[background-color,border-color,color,transform] duration-150 ease-soft active:scale-[0.98] disabled:active:scale-100'

export const btnPrimary = `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-ink px-6 py-3 text-[14px] text-shell hover:bg-clay disabled:cursor-not-allowed disabled:bg-ink/40 ${press}`

export const btnSecondary = `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-ink/20 bg-transparent px-5 py-3 text-[14px] text-ink hover:border-ink disabled:cursor-not-allowed disabled:text-muted disabled:hover:border-ink/20 ${press}`

export const btnQuiet =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-[14px] text-ink transition-colors duration-150 ease-soft hover:bg-sand disabled:cursor-not-allowed disabled:text-muted disabled:hover:bg-transparent'

export const btnPrimaryLg = `inline-flex min-h-[52px] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-ink px-7 text-[15px] text-shell hover:bg-clay disabled:cursor-not-allowed disabled:bg-ink/40 ${press}`

export const btnSecondaryLg = `inline-flex min-h-[52px] items-center justify-center gap-2 whitespace-nowrap rounded-full border border-ink/25 px-7 text-[15px] text-ink hover:border-ink disabled:cursor-not-allowed disabled:text-muted ${press}`

export const btnOnClay = `inline-flex min-h-[52px] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-shell px-7 text-[15px] text-ink hover:bg-blush ${press}`

/*
 * Breekt nooit uit zijn vak: het label mag krimpen (`btnPriceLabel`), het bedrag niet. Op smalle
 * telefoons (360px) iets minder binnenruimte en een kleiner bedrag; zonder dat duwde "Protocol in
 * de mand · € 201" de hele pagina breder dan het scherm.
 */
export const btnPrice = `flex min-h-[52px] w-full min-w-0 max-w-full items-center justify-between gap-3 rounded-full bg-ink px-5 text-[15px] text-shell hover:bg-clay disabled:cursor-not-allowed disabled:bg-ink/40 sm:gap-4 sm:px-6 ${press}`

/** `btnPrice` op een donker vlak (de volgende stap in Mijn omgeving). */
export const btnPriceLight = `flex min-h-[52px] w-full min-w-0 max-w-full items-center justify-between gap-3 rounded-full bg-shell px-5 text-[15px] text-ink hover:bg-blush disabled:cursor-not-allowed disabled:opacity-60 sm:gap-4 sm:px-6 ${press}`

/** Hoofdactie op een donker (ink) vlak. */
export const btnOnDark = `inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-shell px-6 text-[14px] text-ink hover:bg-blush disabled:cursor-not-allowed disabled:opacity-60 ${press}`

/** Tweede actie op een donker (ink) vlak. */
export const btnOnDarkQuiet = `inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-full border border-shell/30 px-5 text-[14px] text-shell hover:border-shell disabled:cursor-not-allowed disabled:opacity-50 ${press}`

/** Gelijkwaardige snelle acties naast elkaar (opschuiven, overslaan, pauzeren). */
export const btnOutline = `inline-flex min-h-[44px] items-center justify-center gap-2 whitespace-nowrap rounded-full border border-ink/15 bg-shell px-4 text-[14px] text-ink hover:border-ink disabled:cursor-not-allowed disabled:opacity-50 ${press}`

/** Het label links in `btnPrice`/`btnPriceBar`: krimpt als het moet, in plaats van eruit te lopen. */
export const btnPriceLabel = 'flex min-w-0 items-center gap-2 truncate'

/** `btnPrice` in de zwevende balk onderin (48px, past naast de rand van de balk). */
export const btnPriceBar = `flex min-h-[48px] w-full min-w-0 items-center justify-between gap-3 rounded-full bg-ink px-5 text-[14px] text-shell active:bg-clay disabled:cursor-not-allowed disabled:bg-ink/40 ${press}`

/** Rustige variant in de zwevende balk (uitverkocht: vraag om een alternatief). */
export const btnPriceBarQuiet = `flex min-h-[48px] w-full items-center justify-between gap-3 rounded-full border border-ink/20 px-5 text-[14px] text-ink active:bg-sand ${press}`

/** Het bedrag rechts in `btnPrice`. */
export const btnPriceAmount = 'shrink-0 whitespace-nowrap font-display text-lg tabular-nums sm:text-xl'

/**
 * Het ronde plusje om één product toe te voegen (kaart, klachtenkiezer, journal, mand).
 * Gebruik het onderdeel `shop/shared/AddButton.tsx`; dit is alleen de vorm.
 */
export const btnAddRound = (added: boolean) =>
  `relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border transition-[background-color,color,border-color,transform] duration-150 ease-soft active:scale-95 disabled:pointer-events-none disabled:opacity-40 ${
    added ? 'border-ink bg-ink text-shell' : 'border-ink/20 text-ink hover:border-ink hover:bg-ink hover:text-shell'
  }`

/**
 * Veld: zachte clay-ring bij focus (ook met de muis: je ziet waar je typt), en een
 * clay-rand zodra het veld als fout gemarkeerd is (aria-invalid).
 */
export const inputCls =
  'w-full border border-line bg-shell px-4 py-3.5 text-[16px] text-ink placeholder:text-muted transition-[border-color,box-shadow] duration-150 ease-soft focus:border-clay focus:outline-none focus:ring-4 focus:ring-clay/15 aria-[invalid=true]:border-clay sm:text-[15px]'

export const labelCls = 'mb-1.5 block text-[14px] text-ink'

/** Aan/uit-kleuren voor alles wat je kiest: filterchips, dagen, bedragen. */
export const choiceState = (active: boolean) =>
  `border transition-colors duration-150 ease-soft ${
    active ? 'border-ink bg-ink text-shell' : 'border-line text-muted hover:border-ink hover:text-ink'
  }`

/** Filterchip (webshop, journal, salon, tijden): ~40px hoog, dus goed te tikken. */
export const chipCls = (active: boolean) =>
  `whitespace-nowrap rounded-full px-4 py-2.5 text-[13px] ${choiceState(active)}`
